import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { paqueteService, Paquete, DespacharPaquetePayload } from '../../services/paqueteService';
import { sucursalService } from '../../services/sucursalService';
import { clienteService } from '../../services/clienteService';
import { useMutation } from 'react-query';

const PaqueteSalida = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState<string>('preparado_salida');
  const [clienteFilter, setClienteFilter] = useState<number | undefined>(undefined);
  const [diasMinFilter, setDiasMinFilter] = useState<number | undefined>(undefined);
  const [selectedPaquetes, setSelectedPaquetes] = useState<Set<number>>(new Set());
  const [showDespachoModal, setShowDespachoModal] = useState(false);
  const [despachoData, setDespachoData] = useState<DespacharPaquetePayload>({
    numeroGuiaSalida: '',
    paqueteriaSalida: '',
    notas: ''
  });

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: clientesData } = useQuery(
    'clientes',
    () => clienteService.getAll(1, 200)
  );

  const { data, isLoading } = useQuery(
    ['paquetes-salida', page, estadoFilter, clienteFilter, diasMinFilter],
    () => paqueteService.getAll(
      page,
      50, // Más paquetes por página para selección múltiple
      estadoFilter || undefined,
      'externo', // Solo externos para salida
      undefined,
      clienteFilter,
      undefined,
      undefined,
      diasMinFilter
    )
  );

  const paquetes = data?.data.paquetes || [];
  const paquetesListos = paquetes.filter(p => 
    p.estado === 'preparado_salida' || p.estado === 'en_almacen'
  );

  const handleToggleSelect = (paqueteId: number) => {
    const newSelected = new Set(selectedPaquetes);
    if (newSelected.has(paqueteId)) {
      newSelected.delete(paqueteId);
    } else {
      newSelected.add(paqueteId);
    }
    setSelectedPaquetes(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPaquetes.size === paquetesListos.length) {
      setSelectedPaquetes(new Set());
    } else {
      setSelectedPaquetes(new Set(paquetesListos.map(p => p.id)));
    }
  };

  const despacharMutation = useMutation(
    (payload: { id: number; data: DespacharPaquetePayload }) =>
      paqueteService.despachar(payload.id, payload.data),
    {
      onSuccess: () => {
        toast.success('Paquete despachado correctamente');
        queryClient.invalidateQueries(['paquetes-salida']);
        queryClient.invalidateQueries(['paquetes']);
        setSelectedPaquetes(new Set());
        setShowDespachoModal(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al despachar paquete');
      }
    }
  );

  const handleDespacharIndividual = (paquete: Paquete) => {
    setSelectedPaquetes(new Set([paquete.id]));
    setShowDespachoModal(true);
  };

  const handleDespacharSeleccionados = () => {
    if (selectedPaquetes.size === 0) {
      toast.warning('Selecciona al menos un paquete');
      return;
    }
    setShowDespachoModal(true);
  };

  const confirmarDespacho = () => {
    if (!despachoData.numeroGuiaSalida || !despachoData.paqueteriaSalida) {
      toast.error('Completa número de guía y paquetería');
      return;
    }

    // Despachar todos los seleccionados
    const promises = Array.from(selectedPaquetes).map(id =>
      despacharMutation.mutateAsync({
        id,
        data: despachoData
      })
    );

    Promise.all(promises)
      .then(() => {
        toast.success(`${selectedPaquetes.size} paquete(s) despachado(s) correctamente`);
        setSelectedPaquetes(new Set());
        setShowDespachoModal(false);
        setDespachoData({ numeroGuiaSalida: '', paqueteriaSalida: '', notas: '' });
      })
      .catch(() => {
        // Error ya manejado en onError
      });
  };

  const paqueterias = [
    'FedEx', 'DHL', 'UPS', 'Estafeta', 'Paquetexpress',
    'Redpack', 'Sendex', 'Correos de México', 'Otro'
  ];

  const totalMontoSeleccionados = Array.from(selectedPaquetes)
    .reduce((sum, id) => {
      const paquete = paquetes.find(p => p.id === id);
      const monto = typeof paquete?.montoAlmacenaje === 'string' ? parseFloat(paquete.montoAlmacenaje) : (paquete?.montoAlmacenaje || 0);
      return sum + (isNaN(monto) ? 0 : monto);
    }, 0);

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      preparado_salida: 'bg-green-100 text-green-800',
      en_almacen: 'bg-blue-100 text-blue-800',
      en_servicio: 'bg-yellow-100 text-yellow-800',
      recibido_centro: 'bg-gray-100 text-gray-800',
      despachado: 'bg-purple-100 text-purple-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Salida de Paquetes</h1>
          <p className="text-gray-600 mt-1">Despacho y consolidación de paquetes</p>
        </div>
        <button
          onClick={() => navigate('/paquetes')}
          className="px-4 py-2 text-gray-700 border rounded hover:bg-gray-50"
        >
          ← Volver
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel izquierdo: Lista de paquetes */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-lg">
          <div className="p-4 border-b space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Paquetes Listos para Salir</h2>
              {selectedPaquetes.size > 0 && (
                <span className="text-sm text-primary-600 font-medium">
                  {selectedPaquetes.size} seleccionado(s)
                </span>
              )}
            </div>

            <div className="flex space-x-4 flex-wrap">
              <select
                value={estadoFilter}
                onChange={(e) => {
                  setEstadoFilter(e.target.value);
                  setPage(1);
                }}
                className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="preparado_salida">Preparados para Salida</option>
                <option value="en_almacen">En Almacén</option>
                <option value="">Todos los estados</option>
              </select>

              <select
                value={clienteFilter || ''}
                onChange={(e) => {
                  setClienteFilter(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos los clientes</option>
                {clientesData?.data.clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </option>
                ))}
              </select>

              <select
                value={diasMinFilter || ''}
                onChange={(e) => {
                  setDiasMinFilter(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos</option>
                <option value="7">Más de 7 días</option>
                <option value="14">Más de 14 días</option>
                <option value="30">Más de 30 días</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">Cargando...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedPaquetes.size === paquetesListos.length && paquetesListos.length > 0}
                          onChange={handleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paquetesListos.length > 0 ? (
                      paquetesListos.map((paquete) => (
                        <tr
                          key={paquete.id}
                          className={`hover:bg-gray-50 ${
                            selectedPaquetes.has(paquete.id) ? 'bg-primary-50' : ''
                          } ${paquete.diasAlmacenaje >= 7 ? 'bg-yellow-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedPaquetes.has(paquete.id)}
                              onChange={() => handleToggleSelect(paquete.id)}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {paquete.numeroTracking}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {paquete.cliente?.nombre || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div>{paquete.destinatarioNombre}</div>
                            <div className="text-xs text-gray-500">{paquete.destinatarioTelefono}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={paquete.diasAlmacenaje >= 7 ? 'font-bold text-red-600' : ''}>
                              {paquete.diasAlmacenaje} días
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            ${(typeof paquete.montoAlmacenaje === 'string' ? parseFloat(paquete.montoAlmacenaje) : (paquete.montoAlmacenaje || 0)).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(paquete.estado)}`}>
                              {paquete.estado.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleDespacharIndividual(paquete)}
                              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                            >
                              Despachar
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                          No hay paquetes listos para salir
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {data?.data.pagination && (
                <div className="p-4 border-t flex justify-between items-center">
                  <div className="text-sm text-gray-700">
                    Mostrando {((page - 1) * 50) + 1} a {Math.min(page * 50, data.data.pagination.total)} de {data.data.pagination.total}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= data.data.pagination.totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Panel derecho: Acciones */}
        <div className="lg:col-span-1 space-y-6">
          {/* Resumen de selección */}
          {selectedPaquetes.size > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Resumen</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Paquetes seleccionados:</span>
                  <span className="font-medium">{selectedPaquetes.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total almacenaje:</span>
                  <span className="font-medium text-primary-600">${totalMontoSeleccionados.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Acciones */}
          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Acciones</h3>
            
            <button
              onClick={handleDespacharSeleccionados}
              disabled={selectedPaquetes.size === 0}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Despachar Seleccionados ({selectedPaquetes.size})
            </button>

            <button
              onClick={() => navigate('/paquetes/envios')}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Crear Envío Consolidado
            </button>

            <button
              onClick={() => navigate('/paquetes/entrada')}
              className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              + Registrar Nuevo Paquete
            </button>
          </div>

          {/* Estadísticas rápidas */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Estadísticas</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Preparados:</span>
                <span className="font-medium">
                  {paquetes.filter(p => p.estado === 'preparado_salida').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">En almacén:</span>
                <span className="font-medium">
                  {paquetes.filter(p => p.estado === 'en_almacen').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Alertas (&gt;7 días):</span>
                <span className="font-medium text-red-600">
                  {paquetes.filter(p => p.diasAlmacenaje >= 7).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de despacho */}
      {showDespachoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Despachar {selectedPaquetes.size} Paquete(s)
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Guía de Salida *
                </label>
                <input
                  type="text"
                  value={despachoData.numeroGuiaSalida}
                  onChange={(e) => setDespachoData({ ...despachoData, numeroGuiaSalida: e.target.value })}
                  placeholder="Ej: 1234567890"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paquetería de Salida *
                </label>
                <select
                  value={despachoData.paqueteriaSalida}
                  onChange={(e) => setDespachoData({ ...despachoData, paqueteriaSalida: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecciona paquetería</option>
                  {paqueterias.map((pq) => (
                    <option key={pq} value={pq}>{pq}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas (Opcional)
                </label>
                <textarea
                  value={despachoData.notas}
                  onChange={(e) => setDespachoData({ ...despachoData, notas: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {selectedPaquetes.size > 0 && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    Total a despachar: <span className="font-medium">{selectedPaquetes.size}</span> paquete(s)
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Monto total almacenaje: <span className="font-medium text-primary-600">${totalMontoSeleccionados.toFixed(2)}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDespachoModal(false);
                  setDespachoData({ numeroGuiaSalida: '', paqueteriaSalida: '', notas: '' });
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarDespacho}
                disabled={despacharMutation.isLoading || !despachoData.numeroGuiaSalida || !despachoData.paqueteriaSalida}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {despacharMutation.isLoading ? 'Despachando...' : 'Confirmar Despacho'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaqueteSalida;

